import React from "react";
import { SpsButton } from "@spscommerce/ds-react";
import { ButtonKind } from "@spscommerce/ds-shared";
import { AppRouteProps } from "../../App";
import { useSpsLanguage } from "@spscommerce/i18n";

export type ConfirmLogoutProps = Partial<AppRouteProps> & Pick<AppRouteProps, "redirectService">;

export function ConfirmLogout({ redirectService }: ConfirmLogoutProps) {
    const { t } = useSpsLanguage();

    function backToRoot() {
        redirectService.messageBus.send<string>("getCurrentUrl").then((urlString) => {
            const url = new URL(urlString);
            if (window.top) {
                window.top.location.href = url.protocol + "//" + url.host;
            }
        });
    }
    function silentlySwitch() {
        redirectService.messageBus.send<string>("getCurrentUrl").then((urlString) => {
            const url = new URL(urlString);
            const params = new URLSearchParams(url.search);
            const orgId = params.get("cp.orgid");
            const unprocessedInitialPath = decodeURIComponent(params.get("cp.initialPath") || "");
            if (unprocessedInitialPath) {
                const initialPathUrl = new URL(unprocessedInitialPath);
                initialPathUrl.searchParams.delete("cp.orgid");
                const innerInitialPath = initialPathUrl.searchParams.get("cp.initialPath");

                let initialPath;
                // Sadly Outlook Safe Links sometimes mangles urls. Luckily can deal with it
                // without requiring user intervention
                if (innerInitialPath != null) {
                    const innerInitialPathUrl = new URL(innerInitialPath);
                    if (innerInitialPathUrl.pathname.startsWith("/auth/confirm-logout")) {
                        // We should never get here, but if we do in Outlooks case, then unwrap
                        const doubleWrappedInitialPathUrl = new URL(
                            innerInitialPathUrl.searchParams.get("cp.initialPath") ?? ""
                        );
                        doubleWrappedInitialPathUrl.searchParams.delete("cp.orgid");
                        initialPath =
                            doubleWrappedInitialPathUrl?.searchParams.get("cp.initialPath");
                    }
                } else {
                    initialPath = initialPathUrl.toString();
                }
                redirectService.messageBus.send("silentlySwitchToOrg", { orgId, initialPath });
            }
        });
    }

    return (
        <>
            <div className="auth-box">
                <div className="auth-logo">
                    <div className="logo"></div>
                </div>
                <div className="auth-content">
                    <h1 className="auth-content-header">
                        {t("cpauth.cofirmLogout.warningHeader", {
                            defaultValue: "Confirm Change Company",
                        })}
                    </h1>
                    <p>
                        {t("cpauth.cofirmLogout.warning", {
                            defaultValue:
                                "You have requested to log in to a different organization. This will log you out of your current organization and request login information for the new organization. Please save any unsaved work before continuing.",
                        })}
                    </p>
                    <SpsButton
                        className="mr-1 mb-1 auth-button"
                        kind={ButtonKind.CONFIRM}
                        data-testid="confirm-button"
                        onClick={() => {
                            silentlySwitch();
                        }}
                    >
                        {t("cpauth.cofirmLogout.continue", {
                            defaultValue: "Continue to new organization",
                        })}
                    </SpsButton>

                    <SpsButton
                        className="mr-1 mb-1 auth-cancel-button"
                        data-testid="cancel-button"
                        kind={ButtonKind.LINK}
                        onClick={backToRoot}
                    >
                        {t("cpauth.cofirmLogout.return", {
                            defaultValue: "Return to current organization",
                        })}
                    </SpsButton>
                </div>
            </div>
        </>
    );
}
